import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, projectsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import {
  FolderOpen,
  Users,
  DollarSign,
  CheckCircle,
  Clock,
  AlertTriangle,
  Banknote,
  TrendingUp,
  BarChart3,
  ArrowRight,
  Calendar,
  Target,
  FileEdit,
  AlertCircle,
  Pencil,
  FileSignature,
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ title, value, icon: Icon, description, variant = 'default', onClick }) => {
  const variants = {
    default: 'bg-card',
    primary: 'bg-primary/10 border-primary/20',
    warning: 'bg-amber-500/10 border-amber-500/20',
    success: 'bg-emerald-500/10 border-emerald-500/20',
    danger: 'bg-red-500/10 border-red-500/20',
    blue: 'bg-blue-500/10 border-blue-500/20',
  };

  return (
    <Card 
      className={cn(
        'transition-all duration-200 hover:shadow-md',
        variants[variant],
        onClick && 'cursor-pointer hover:scale-[1.02]'
      )} 
      onClick={onClick}
      data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={cn(
            'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0',
            variant === 'primary' && 'bg-primary text-primary-foreground',
            variant === 'warning' && 'bg-amber-500 text-white',
            variant === 'success' && 'bg-emerald-500 text-white',
            variant === 'danger' && 'bg-red-500 text-white',
            variant === 'blue' && 'bg-blue-500 text-white',
            variant === 'default' && 'bg-secondary text-secondary-foreground'
          )}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-muted-foreground truncate">{title}</p>
            <p className="text-xl font-bold truncate">{value}</p>
            {description && (
              <p className="text-xs text-muted-foreground">{description}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

// Card de listagem por categoria
const CategoryCard = ({ title, icon: Icon, projects, color, navigate, emptyMessage }) => {
  const colorClasses = {
    orange: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-600', iconBg: 'bg-amber-500' },
    red: { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-600', iconBg: 'bg-red-500' },
    blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-600', iconBg: 'bg-blue-500' },
    green: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-600', iconBg: 'bg-emerald-500' },
    gray: { bg: 'bg-gray-500/10', border: 'border-gray-500/20', text: 'text-gray-600', iconBg: 'bg-gray-500' },
  };

  const colors = colorClasses[color] || colorClasses.gray;

  return (
    <Card className={cn(colors.bg, colors.border)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center text-white', colors.iconBg)}>
              <Icon className="w-4 h-4" />
            </div>
            {title}
          </CardTitle>
          <Badge variant="secondary" className={colors.text}>{projects.length}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-48">
          {projects.length === 0 ? (
            <p className="text-center text-muted-foreground py-6 text-sm">
              {emptyMessage || 'Nenhum projeto'}
            </p>
          ) : (
            <div className="space-y-2">
              {projects.slice(0, 10).map((projeto) => (
                <div
                  key={projeto.id}
                  className="flex items-center justify-between p-2 rounded-lg bg-background/50 hover:bg-background transition-colors cursor-pointer border"
                  onClick={() => navigate(`/projetos?project=${projeto.id}`)}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{projeto.cliente_nome}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {projeto.etapa_atual_nome || projeto.etapa_atual || '-'}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0 ml-2" />
                </div>
              ))}
              {projects.length > 10 && (
                <p className="text-center text-xs text-muted-foreground pt-2">
                  +{projects.length - 10} projetos
                </p>
              )}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [statusConfig, setStatusConfig] = useState([]);
  const [projetosAntigos, setProjetosAntigos] = useState([]);

  // Calcular dias sem comentário
  const calculateDaysSinceComment = (project) => {
    const observacoes = project.observacoes || [];
    if (observacoes.length === 0) {
      return project.data_inicio ? Math.floor((new Date() - new Date(project.data_inicio)) / (1000 * 60 * 60 * 24)) : 999;
    }
    const ultimaObs = new Date(observacoes[observacoes.length - 1].data);
    return Math.floor((new Date() - ultimaObs) / (1000 * 60 * 60 * 24));
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('agrolink_token');
      
      const [statsRes, projectsRes, statusRes] = await Promise.all([
        dashboardAPI.stats(),
        projectsAPI.list({ status: 'em_andamento' }),
        axios.get(`${API_URL}/api/status-projeto/ativos`, { 
          headers: { Authorization: `Bearer ${token}` } 
        }).catch(() => ({ data: [] })),
      ]);
      
      setStats(statsRes.data);
      setProjects(projectsRes.data);
      setStatusConfig(statusRes.data || []);
      
      // Calcular projetos mais antigos (mais de 30 dias)
      const agora = new Date();
      const projetosComDuracao = projectsRes.data
        .filter(p => p.data_inicio)
        .map(p => ({
          ...p,
          duracao: Math.floor((agora - new Date(p.data_inicio)) / (1000 * 60 * 60 * 24))
        }))
        .sort((a, b) => b.duracao - a.duracao)
        .slice(0, 5);
      setProjetosAntigos(projetosComDuracao);
      
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Categorizar projetos por status
  const getProjectStatus = (project) => {
    const etapaNome = project.etapa_atual_nome || project.etapa_atual || '';
    const temPendencia = project.tem_pendencia || false;
    const diasSemComentario = calculateDaysSinceComment(project);

    for (const status of statusConfig) {
      let match = true;

      if (status.condicao_etapas && status.condicao_etapas.length > 0) {
        if (!status.condicao_etapas.includes(etapaNome)) {
          match = false;
        }
      }

      if (match && status.condicao_pendencia !== null && status.condicao_pendencia !== undefined) {
        if (status.condicao_pendencia !== temPendencia) {
          match = false;
        }
      }

      if (match && status.condicao_dias_sem_comentario !== null && status.condicao_dias_sem_comentario !== undefined) {
        if (diasSemComentario < status.condicao_dias_sem_comentario) {
          match = false;
        }
      }

      if (match && status.condicao_dias_sem_comentario_max !== null && status.condicao_dias_sem_comentario_max !== undefined) {
        if (diasSemComentario > status.condicao_dias_sem_comentario_max) {
          match = false;
        }
      }

      if (match) {
        return status.nome;
      }
    }

    return 'OK';
  };

  // Agrupar projetos por status
  const projetosPendentes = projects.filter(p => getProjectStatus(p) === 'Pendente');
  const projetosTratativa = projects.filter(p => getProjectStatus(p) === 'Tratativa');
  const projetosProjetar = projects.filter(p => getProjectStatus(p) === 'Projetar');
  const projetosAssinatura = projects.filter(p => getProjectStatus(p) === 'Assinatura');
  const projetosOK = projects.filter(p => getProjectStatus(p) === 'OK');

  // Calcular projetos com pendência
  const projetosComPendencia = projects.filter(p => p.tem_pendencia).length;
  const percentualPendencia = projects.length > 0 
    ? Math.round((projetosComPendencia / projects.length) * 100) 
    : 0;

  if (loading && !stats) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Visão geral dos seus projetos de crédito rural</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/projetos')} data-testid="view-projects-btn">
            <FolderOpen className="w-4 h-4 mr-2" />
            Ver Projetos
          </Button>
          <Button onClick={() => navigate('/projetos/novo')} data-testid="new-project-btn">
            <FolderOpen className="w-4 h-4 mr-2" />
            Novo Projeto
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard
          title="Projetos Ativos"
          value={projects.length}
          icon={FolderOpen}
          description="Em andamento"
          variant="primary"
          onClick={() => navigate('/projetos')}
        />
        <StatCard
          title="Clientes"
          value={stats?.total_clientes || 0}
          icon={Users}
          description="Total cadastrados"
          onClick={() => navigate('/clientes')}
        />
        <StatCard
          title="Em Andamento"
          value={formatCurrency(stats?.valor_total_credito || 0)}
          icon={DollarSign}
          description="Crédito em análise"
          variant="primary"
        />
        <StatCard
          title="Creditados"
          value={formatCurrency(stats?.valor_credito_finalizado || 0)}
          icon={CheckCircle}
          description="Projetos finalizados"
          variant="success"
        />
        <StatCard
          title="Serviço"
          value={formatCurrency(stats?.valor_total_servico || 0)}
          icon={Banknote}
          description="Serviços realizados"
          variant="success"
        />
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard
          title="Pendentes"
          value={projetosPendentes.length}
          icon={AlertTriangle}
          description="Com pendência"
          variant="warning"
        />
        <StatCard
          title="Tratativa"
          value={projetosTratativa.length}
          icon={AlertCircle}
          description="Precisam atenção"
          variant="danger"
        />
        <StatCard
          title="Projetar"
          value={projetosProjetar.length}
          icon={Pencil}
          description="Em desenvolvimento"
          variant="blue"
        />
        <StatCard
          title="Assinatura"
          value={projetosAssinatura.length}
          icon={FileSignature}
          description="Aguardando assinatura"
          variant="success"
        />
        <StatCard
          title="OK"
          value={projetosOK.length}
          icon={CheckCircle}
          description="Em dia"
        />
      </div>

      {/* Category Cards with Project Lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <CategoryCard
          title="Pendentes"
          icon={AlertTriangle}
          projects={projetosPendentes}
          color="orange"
          navigate={navigate}
          emptyMessage="Nenhum projeto pendente"
        />
        <CategoryCard
          title="Tratativa"
          icon={AlertCircle}
          projects={projetosTratativa}
          color="red"
          navigate={navigate}
          emptyMessage="Nenhum projeto em tratativa"
        />
        <CategoryCard
          title="Projetar"
          icon={Pencil}
          projects={projetosProjetar}
          color="blue"
          navigate={navigate}
          emptyMessage="Nenhum projeto para desenvolver"
        />
        <CategoryCard
          title="Assinatura"
          icon={FileSignature}
          projects={projetosAssinatura}
          color="green"
          navigate={navigate}
          emptyMessage="Nenhum projeto para assinar"
        />
      </div>

      {/* Projetos Mais Antigos */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Projetos com Mais Tempo
              </CardTitle>
              <CardDescription>Projetos que precisam de atenção</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/projetos')}>
              Ver todos <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {projetosAntigos.length === 0 ? (
              <p className="text-center text-muted-foreground py-8 col-span-full">
                Nenhum projeto encontrado
              </p>
            ) : (
              projetosAntigos.map((projeto) => (
                <div
                  key={projeto.id}
                  className={cn(
                    "flex flex-col p-3 rounded-lg border transition-colors cursor-pointer",
                    projeto.duracao > 90 && "bg-red-50 border-red-200 hover:bg-red-100",
                    projeto.duracao > 60 && projeto.duracao <= 90 && "bg-amber-50 border-amber-200 hover:bg-amber-100",
                    projeto.duracao <= 60 && "bg-card hover:bg-muted/50"
                  )}
                  onClick={() => navigate(`/projetos?project=${projeto.id}`)}
                  data-testid={`old-project-${projeto.id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className={cn(
                      "w-4 h-4",
                      projeto.duracao > 90 && "text-red-500",
                      projeto.duracao > 60 && projeto.duracao <= 90 && "text-amber-500",
                      projeto.duracao <= 60 && "text-emerald-500"
                    )} />
                    <span className={cn(
                      "font-bold text-lg",
                      projeto.duracao > 90 && "text-red-500",
                      projeto.duracao > 60 && projeto.duracao <= 90 && "text-amber-500",
                      projeto.duracao <= 60 && "text-emerald-500"
                    )}>
                      {projeto.duracao} dias
                    </span>
                  </div>
                  <p className="font-medium text-sm truncate">{projeto.cliente_nome}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {projeto.etapa_atual_nome || projeto.etapa_atual || 'Sem etapa'}
                  </p>
                  {projeto.tem_pendencia && (
                    <Badge variant="warning" className="mt-2 text-xs bg-amber-500/10 text-amber-600 border-amber-500/20 w-fit">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Pendente
                    </Badge>
                  )}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Ações Rápidas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Button 
              variant="outline" 
              className="h-20 flex-col gap-2"
              onClick={() => navigate('/projetos/novo')}
            >
              <FolderOpen className="w-6 h-6" />
              <span className="text-xs">Novo Projeto</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex-col gap-2"
              onClick={() => navigate('/clientes/novo')}
            >
              <Users className="w-6 h-6" />
              <span className="text-xs">Novo Cliente</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex-col gap-2"
              onClick={() => navigate('/relatorios')}
            >
              <BarChart3 className="w-6 h-6" />
              <span className="text-xs">Relatórios</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex-col gap-2"
              onClick={() => navigate('/projetos')}
            >
              <Target className="w-6 h-6" />
              <span className="text-xs">Ver Projetos</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
